import { useDataIndicators } from '@ctw/shared/api/fhir/data-indicator';
import type { DataIndicatorGraphqlResponse } from '@ctw/shared/api/fqs/queries/data-indicator';
import type { ZAPTabName } from '@ctw/shared/content/zus-aggregated-profile';
import { useMemo } from 'react';
import { usePatientContext } from '../context/patient-provider';

// Maps whether each ZAP resource has any records
export function useHasDataRecordsByResource(
  resources: ReadonlyArray<ZAPTabName>,
): Map<ZAPTabName, boolean> {
  const numRecordsToLookForData = 1;
  usePatientContext(); // Require patient context
  const finalResources = resources;
  const query = useDataIndicators(
    numRecordsToLookForData,
    finalResources.length > 0,
    finalResources,
  );

  // Memoize on the actual inputs — the resource list plus the query's data and loading state — so
  // the returned Map keeps a stable identity across renders when nothing has changed, and only
  // recomputes when the underlying data indicators actually update. Returning a fresh Map every
  // render made every downstream `useMemo`/effect keyed on it (e.g. `useZapSections` → the
  // sub-navigation links) re-run on every render.
  return useMemo(() => {
    const map = new Map<ZAPTabName, boolean>();
    // Default every tab to `true` so nothing flashes "no data" while loading — we never want to
    // incorrectly show that there is no data.
    finalResources.forEach((zapTabName) => {
      map.set(zapTabName, true);
    });

    if (!query.isLoading) {
      finalResources.forEach((resource) => {
        const edges = handleResourceType(resource, query.data);
        // Only mark a resource as having no data when the query returned an empty edge set.
        if (edges && edges.length === 0) {
          map.set(resource, false);
        }
      });
    }

    return map;
  }, [finalResources, query.isLoading, query.data]);
}

const handleResourceType = (
  resource: ZAPTabName,
  data: DataIndicatorGraphqlResponse | undefined,
) => {
  switch (resource) {
    case 'conditions-all':
      return data?.ConditionConnection.edges;
    case 'allergies':
      return data?.AllergyIntoleranceConnection.edges;
    case 'diagnostic-reports':
      return data?.DiagnosticReportConnection.edges;
    case 'documents':
      return data?.DocumentReferenceConnection.edges;
    case 'demographics':
      return data?.PatientConnection.edges;
    case 'referrals':
      return data?.ServiceRequestConnection.edges;
    case 'encounters':
      return data?.EncounterConnection.edges;
    case 'immunizations':
      return data?.ImmunizationConnection.edges;
    // Only gotcha here is that we don't filter out MedicationAdministration
    case 'medications-all':
      return data?.MedicationStatementConnection.edges;
    case 'vitals':
      return data?.ObservationConnection.edges;
    case 'care-team':
      return data?.CareTeamConnection.edges;
    default:
      return [];
  }
};
